/**
 * RelatorioDISCProfissional - Relatório DISC Profissional Completo
 * Modelo inspirado em relatórios corporativos de alto padrão
 * Multi-página com gráficos, descrições detalhadas e pontos de desenvolvimento
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, 
  Download, 
  Loader2, 
  User,
  Briefcase,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Target,
  Heart,
  Zap,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

// Cores oficiais DISC
const DISC_COLORS = {
  D: '#dc2626', // Vermelho
  I: '#f59e0b', // Amarelo/Laranja
  S: '#22c55e', // Verde
  C: '#3b82f6', // Azul
  primary: '#1e40af', // Azul escuro para cabeçalhos
  secondary: '#64748b', // Cinza para textos secundários
  background: '#f8fafc', // Fundo claro
  white: '#ffffff'
};

// Perfis DISC completos com descrições profissionais
const DISC_PROFILES = {
  D: {
    nome: 'Dominância',
    nomeCompleto: 'Dominância (D)',
    icone: '⚡',
    cor: DISC_COLORS.D,
    palavrasChave: ['Resultados', 'Competição', 'Decisão', 'Liderança', 'Desafio'],
    caracteristicasPrincipais: [
      'Orientado para resultados e metas',
      'Tomador de decisões rápido e assertivo',
      'Gosta de assumir o controle das situações',
      'Competitivo e determinado',
      'Direto e objetivo na comunicação',
      'Focado em resolver problemas'
    ],
    pontosFortes: [
      'Liderança natural em momentos de crise',
      'Capacidade de tomar decisões sob pressão',
      'Foco inabalável em objetivos',
      'Iniciativa e proatividade',
      'Determinação para superar obstáculos'
    ],
    pontosAtencao: [
      'Pode parecer impaciente ou autoritário',
      'Tendência a desconsiderar sentimentos alheios',
      'Necessidade de delegar mais e confiar na equipe',
      'Melhorar a escuta ativa',
      'Desenvolver maior diplomacia'
    ],
    ambiente: 'Prefere ambientes dinâmicos, com autonomia para tomar decisões, metas claras e desafiadoras, e liberdade para inovar e liderar.',
    comunicacao: 'Seja direto, objetivo e focado em resultados. Evite rodeios. Apresente opções e deixe-o decidir.',
    motivadores: ['Desafios', 'Autonomia', 'Resultados', 'Reconhecimento', 'Controle'],
    estresse: 'Rotina, falta de controle, lentidão nas decisões, detalhes excessivos'
  },
  I: {
    nome: 'Influência',
    nomeCompleto: 'Influência (I)',
    icone: '☀️',
    cor: DISC_COLORS.I,
    palavrasChave: ['Entusiasmo', 'Otimismo', 'Colaboração', 'Comunicação', 'Reconhecimento'],
    caracteristicasPrincipais: [
      'Comunicativo e entusiasta',
      'Otimista e motivador',
      'Gosta de trabalhar em equipe',
      'Persuasivo e carismático',
      'Criativo e inovador',
      'Sociável e expansivo'
    ],
    pontosFortes: [
      'Habilidade de motivar e inspirar pessoas',
      'Excelente networking e relacionamentos',
      'Criatividade e pensamento inovador',
      'Capacidade de persuasão',
      'Ambiente de trabalho positivo'
    ],
    pontosAtencao: [
      'Pode ter dificuldade com detalhes e organização',
      'Tendência a se comprometer demais',
      'Necessidade de foco e priorização',
      'Melhorar gestão do tempo',
      'Desenvolver mais objetividade'
    ],
    ambiente: 'Prefere ambientes colaborativos, com liberdade para expressar ideias, interação social frequente e reconhecimento público.',
    comunicacao: 'Seja amigável e aberto. Permita que ele se expresse. Reconheça suas contribuições publicamente.',
    motivadores: ['Reconhecimento social', 'Liberdade de expressão', 'Trabalho em equipe', 'Novidades', 'Ambiente positivo'],
    estresse: 'Isolamento, rejeição social, rotina repetitiva, críticas públicas'
  },
  S: {
    nome: 'Estabilidade',
    nomeCompleto: 'Estabilidade (S)',
    icone: '🌿',
    cor: DISC_COLORS.S,
    palavrasChave: ['Harmonia', 'Paciência', 'Lealdade', 'Suporte', 'Consistência'],
    caracteristicasPrincipais: [
      'Paciente e bom ouvinte',
      'Leal e confiável',
      'Busca harmonia e estabilidade',
      'Trabalha bem em equipe',
      'Consistente e previsível',
      'Solidário e prestativo'
    ],
    pontosFortes: [
      'Mantém a coesão e harmonia da equipe',
      'Confiabilidade e persistência',
      'Excelente suporte aos colegas',
      'Paciência para tarefas de longo prazo',
      'Habilidade de mediar conflitos'
    ],
    pontosAtencao: [
      'Pode ter dificuldade com mudanças rápidas',
      'Tendência a evitar conflitos necessários',
      'Necessidade de maior assertividade',
      'Melhorar capacidade de tomar iniciativa',
      'Desenvolver adaptabilidade'
    ],
    ambiente: 'Prefere ambientes estáveis, com processos bem definidos, relacionamentos de longo prazo e mudanças graduais.',
    comunicacao: 'Seja paciente e mostre interesse genuíno. Dê tempo para processar mudanças. Evite pressão excessiva.',
    motivadores: ['Estabilidade', 'Relacionamentos', 'Reconhecimento sincero', 'Segurança', 'Tempo para adaptação'],
    estresse: 'Mudanças bruscas, conflitos, pressão por resultados rápidos, ambientes caóticos'
  },
  C: {
    nome: 'Conformidade',
    nomeCompleto: 'Conformidade (C)',
    icone: '📊',
    cor: DISC_COLORS.C,
    palavrasChave: ['Precisão', 'Qualidade', 'Análise', 'Procedimentos', 'Exatidão'],
    caracteristicasPrincipais: [
      'Analítico e detalhista',
      'Focado em qualidade e precisão',
      'Sistemático e organizado',
      'Segue regras e procedimentos',
      'Pensa antes de agir',
      'Busca a perfeição'
    ],
    pontosFortes: [
      'Atenção excepcional aos detalhes',
      'Análise crítica e pensamento lógico',
      'Padrões elevados de qualidade',
      'Organização e planejamento',
      'Identificação de riscos e erros'
    ],
    pontosAtencao: [
      'Pode ser excessivamente crítico',
      'Tendência à paralisia por análise',
      'Necessidade de maior flexibilidade',
      'Melhorar velocidade de decisão',
      'Desenvolver tolerância a imperfeições'
    ],
    ambiente: 'Prefere ambientes organizados, com processos claros, tempo para análise e padrões de qualidade definidos.',
    comunicacao: 'Seja preciso e baseado em fatos. Forneça dados e evidências. Dê tempo para análise antes de decisões.',
    motivadores: ['Qualidade', 'Precisão', 'Reconhecimento técnico', 'Independência', 'Processos claros'],
    estresse: 'Ambiguidade, pressão por decisões rápidas, críticas ao trabalho, falta de informações'
  }
};

// Combinações de perfis
const PERFIL_COMBINACOES = {
  'DI': { nome: 'Inspirador', descricao: 'Combina assertividade com entusiasmo. Lidera motivando e engajando pessoas.' },
  'DIS': { nome: 'Estrategista Relacional', descricao: 'Equilibra resultados com relacionamentos e consistência.' },
  'DC': { nome: 'Realizador', descricao: 'Une determinação com precisão. Focado em resultados de alta qualidade.' },
  'ID': { nome: 'Promotor', descricao: 'Comunicativo e orientado para ação. Persuasivo e direto.' },
  'IS': { nome: 'Conselheiro', descricao: 'Amigável e solidário. Excelente para relacionamentos e suporte.' },
  'IC': { nome: 'Comunicador Analítico', descricao: 'Criativo com atenção aos detalhes. Inovador mas metódico.' },
  'SD': { nome: 'Especialista', descricao: 'Paciente mas determinado. Consistente na busca de objetivos.' },
  'SI': { nome: 'Facilitador', descricao: 'Harmonizador natural. Cria ambientes positivos e colaborativos.' },
  'SC': { nome: 'Detalhista Intelectual', descricao: 'Metódico e confiável. Focado em qualidade e consistência.' },
  'CD': { nome: 'Perfeccionista Assertivo', descricao: 'Exigente e direto. Busca excelência com determinação.' },
  'CI': { nome: 'Avaliador', descricao: 'Analítico mas comunicativo. Equilibra dados com relacionamentos.' },
  'CS': { nome: 'Especialista Técnico', descricao: 'Preciso e paciente. Excelente para trabalhos que requerem atenção.' },
  'D': { nome: 'Dominante Puro', descricao: 'Altamente assertivo e focado em resultados.' },
  'I': { nome: 'Influente Puro', descricao: 'Altamente comunicativo e entusiasta.' },
  'S': { nome: 'Estável Puro', descricao: 'Altamente paciente e consistente.' },
  'C': { nome: 'Conforme Puro', descricao: 'Altamente analítico e preciso.' }
};

// Função para determinar o perfil combinado
const getPerfilCombinado = (scores) => {
  const sorted = Object.entries(scores)
    .sort(([,a], [,b]) => b - a)
    .filter(([,score]) => score >= 50);
  
  if (sorted.length === 0) return { nome: 'Equilibrado', descricao: 'Perfil com traços equilibrados entre todas as dimensões.' };
  
  const combo = sorted.map(([letra]) => letra).join('');
  return PERFIL_COMBINACOES[combo] || PERFIL_COMBINACOES[combo[0]] || { nome: 'Misto', descricao: 'Combinação única de traços comportamentais.' };
};

// Função para gerar PDF profissional multi-página
const generateProfessionalPDF = async (data, psicologo) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  
  const scores = data.scores || { D: 25, I: 25, S: 25, C: 25 };
  const dominantProfile = DISC_PROFILES[data.dominant];
  const perfilCombinado = getPerfilCombinado(scores);
  
  // Helper para adicionar nova página
  const addNewPage = () => {
    pdf.addPage();
    addHeader();
    addFooter();
    return margin + 25;
  };
  
  // Header padrão
  const addHeader = () => {
    pdf.setFillColor(30, 64, 175); // Azul escuro
    pdf.rect(0, 0, pageWidth, 20, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('G2eGente', margin, 13);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Relatório DISC Comportamental', pageWidth - margin, 13, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
  };
  
  // Footer padrão
  const addFooter = () => {
    pdf.setFillColor(241, 245, 249);
    pdf.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text(`${data.nome} - Avaliação: ${data.dataAvaliacao}`, margin, pageHeight - 6);
    pdf.text(`Página ${pdf.getNumberOfPages()}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
  };
  
  // Função para desenhar barra de gráfico
  const drawBar = (x, y, width, height, score, color, label) => {
    const barHeight = (score / 100) * height;
    
    // Fundo da barra
    pdf.setFillColor(241, 245, 249);
    pdf.rect(x, y, width, height, 'F');
    
    // Barra colorida
    const rgb = hexToRgb(color);
    pdf.setFillColor(rgb.r, rgb.g, rgb.b);
    pdf.rect(x, y + (height - barHeight), width, barHeight, 'F');
    
    // Label
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(rgb.r, rgb.g, rgb.b);
    pdf.text(label, x + width/2, y + height + 8, { align: 'center' });
    
    // Score
    pdf.setFontSize(10);
    pdf.setTextColor(50, 50, 50);
    pdf.text(`${score}%`, x + width/2, y - 3, { align: 'center' });
    pdf.setTextColor(0, 0, 0);
  };
  
  // Helper para converter hex para RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };
  
  // ================== PÁGINA 1: CAPA ==================
  // Background gradiente
  pdf.setFillColor(30, 64, 175);
  pdf.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Círculo decorativo
  pdf.setFillColor(59, 130, 246);
  pdf.circle(pageWidth - 40, 60, 80, 'F');
  pdf.setFillColor(96, 165, 250);
  pdf.circle(30, pageHeight - 50, 60, 'F');
  
  // Título principal
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.setFont('helvetica', 'bold');
  pdf.text('RELATÓRIO', pageWidth/2, 80, { align: 'center' });
  pdf.setFontSize(28);
  pdf.text('DISC COMPORTAMENTAL', pageWidth/2, 95, { align: 'center' });
  
  // Subtítulo
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Análise de Perfil Comportamental', pageWidth/2, 115, { align: 'center' });
  
  // Box branco com informações
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(margin + 20, 140, contentWidth - 40, 80, 5, 5, 'F');
  
  pdf.setTextColor(30, 64, 175);
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.text(data.nome.toUpperCase(), pageWidth/2, 165, { align: 'center' });
  
  pdf.setTextColor(100, 116, 139);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  if (data.cargo) {
    pdf.text(data.cargo, pageWidth/2, 180, { align: 'center' });
  }
  pdf.text(`Data da Avaliação: ${data.dataAvaliacao}`, pageWidth/2, 195, { align: 'center' });
  pdf.text(`Perfil Predominante: ${perfilCombinado.nome}`, pageWidth/2, 210, { align: 'center' });
  
  // Logo/Branding
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont('helvetica', 'bold');
  pdf.text('G2eGente', pageWidth/2, pageHeight - 40, { align: 'center' });
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Plataforma de Recrutamento Ético', pageWidth/2, pageHeight - 30, { align: 'center' });
  
  // ================== PÁGINA 2: O QUE É DISC ==================
  addNewPage();
  let y = margin + 30;
  
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('O QUE É O DISC?', margin, y);
  y += 12;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  
  const discIntro = [
    'O DISC é uma metodologia de análise comportamental desenvolvida pelo psicólogo William Moulton',
    'Marston na década de 1920. Esta ferramenta identifica quatro dimensões comportamentais principais:',
    '',
    '• Dominância (D): Como a pessoa lida com problemas e desafios',
    '• Influência (I): Como a pessoa lida com pessoas e influencia outros',
    '• Estabilidade (S): Como a pessoa lida com mudanças e ritmo do ambiente',
    '• Conformidade (C): Como a pessoa lida com regras e procedimentos',
    '',
    'É importante ressaltar que o DISC mede COMPORTAMENTOS observáveis, não personalidade ou',
    'inteligência. Não existem perfis bons ou ruins - cada combinação traz pontos fortes únicos e',
    'áreas de desenvolvimento específicas.'
  ];
  
  discIntro.forEach(line => {
    pdf.text(line, margin, y);
    y += 6;
  });
  
  y += 10;
  
  // Quadrantes DISC
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('OS QUATRO QUADRANTES', margin, y);
  y += 15;
  
  const quadrantWidth = (contentWidth - 10) / 2;
  const quadrantHeight = 45;
  
  // Quadrante D
  pdf.setFillColor(254, 226, 226);
  pdf.roundedRect(margin, y, quadrantWidth, quadrantHeight, 3, 3, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(220, 38, 38);
  pdf.text('D - DOMINÂNCIA', margin + 5, y + 10);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text('Direto, Decidido, Determinado', margin + 5, y + 20);
  pdf.text('Foco: Resultados e Desafios', margin + 5, y + 28);
  pdf.text('Medo: Perder o controle', margin + 5, y + 36);
  
  // Quadrante I
  pdf.setFillColor(254, 243, 199);
  pdf.roundedRect(margin + quadrantWidth + 10, y, quadrantWidth, quadrantHeight, 3, 3, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(245, 158, 11);
  pdf.text('I - INFLUÊNCIA', margin + quadrantWidth + 15, y + 10);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text('Inspirador, Interativo, Impressionante', margin + quadrantWidth + 15, y + 20);
  pdf.text('Foco: Pessoas e Entusiasmo', margin + quadrantWidth + 15, y + 28);
  pdf.text('Medo: Rejeição social', margin + quadrantWidth + 15, y + 36);
  
  y += quadrantHeight + 5;
  
  // Quadrante S
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(margin, y, quadrantWidth, quadrantHeight, 3, 3, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(34, 197, 94);
  pdf.text('S - ESTABILIDADE', margin + 5, y + 10);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text('Sincero, Sereno, Solidário', margin + 5, y + 20);
  pdf.text('Foco: Harmonia e Consistência', margin + 5, y + 28);
  pdf.text('Medo: Mudanças bruscas', margin + 5, y + 36);
  
  // Quadrante C
  pdf.setFillColor(219, 234, 254);
  pdf.roundedRect(margin + quadrantWidth + 10, y, quadrantWidth, quadrantHeight, 3, 3, 'F');
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(59, 130, 246);
  pdf.text('C - CONFORMIDADE', margin + quadrantWidth + 15, y + 10);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text('Cuidadoso, Criterioso, Calculista', margin + quadrantWidth + 15, y + 20);
  pdf.text('Foco: Qualidade e Precisão', margin + quadrantWidth + 15, y + 28);
  pdf.text('Medo: Estar errado', margin + quadrantWidth + 15, y + 36);
  
  // ================== PÁGINA 3: SEU PERFIL - GRÁFICO ==================
  addNewPage();
  y = margin + 30;
  
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('SEU PERFIL DISC', margin, y);
  y += 8;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(100, 116, 139);
  pdf.text(`Perfil Identificado: ${perfilCombinado.nome} - ${perfilCombinado.descricao}`, margin, y);
  y += 20;
  
  // Gráfico de barras
  const barWidth = 35;
  const barHeight = 100;
  const barSpacing = 10;
  const startX = margin + 20;
  
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(50, 50, 50);
  pdf.text('Intensidade dos Fatores DISC', pageWidth/2, y, { align: 'center' });
  y += 15;
  
  drawBar(startX, y, barWidth, barHeight, scores.D, DISC_COLORS.D, 'D');
  drawBar(startX + barWidth + barSpacing, y, barWidth, barHeight, scores.I, DISC_COLORS.I, 'I');
  drawBar(startX + (barWidth + barSpacing) * 2, y, barWidth, barHeight, scores.S, DISC_COLORS.S, 'S');
  drawBar(startX + (barWidth + barSpacing) * 3, y, barWidth, barHeight, scores.C, DISC_COLORS.C, 'C');
  
  y += barHeight + 25;
  
  // Interpretação do gráfico
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('Interpretação do Gráfico:', margin + 5, y + 12);
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  
  const highScores = Object.entries(scores).filter(([,v]) => v >= 60).map(([k]) => DISC_PROFILES[k].nome);
  const lowScores = Object.entries(scores).filter(([,v]) => v <= 40).map(([k]) => DISC_PROFILES[k].nome);
  
  let interpretation = `Seus fatores mais elevados são: ${highScores.length > 0 ? highScores.join(', ') : 'Nenhum acima de 60%'}. `;
  interpretation += `Fatores menos expressivos: ${lowScores.length > 0 ? lowScores.join(', ') : 'Nenhum abaixo de 40%'}.`;
  
  const splitInterpretation = pdf.splitTextToSize(interpretation, contentWidth - 10);
  pdf.text(splitInterpretation, margin + 5, y + 24);
  
  // ================== PÁGINA 4: PERFIL DOMINANTE DETALHADO ==================
  addNewPage();
  y = margin + 30;
  
  const rgb = hexToRgb(dominantProfile.cor);
  
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(rgb.r, rgb.g, rgb.b);
  pdf.text(`SEU ESTILO PREDOMINANTE: ${dominantProfile.nome.toUpperCase()}`, margin, y);
  y += 15;
  
  // Box de características
  pdf.setFillColor(rgb.r, rgb.g, rgb.b);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('CARACTERÍSTICAS PRINCIPAIS', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  
  dominantProfile.caracteristicasPrincipais.forEach((carac, i) => {
    pdf.text(`• ${carac}`, margin + 5, y);
    y += 6;
  });
  
  y += 8;
  
  // Palavras-chave
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(margin, y, contentWidth, 20, 3, 3, 'F');
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(rgb.r, rgb.g, rgb.b);
  pdf.text('Palavras-chave:', margin + 5, y + 8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text(dominantProfile.palavrasChave.join(' • '), margin + 40, y + 8);
  y += 28;
  
  // Motivadores
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('O QUE MOTIVA ESTE PERFIL:', margin, y);
  y += 8;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  dominantProfile.motivadores.forEach(mot => {
    pdf.text(`• ${mot}`, margin + 5, y);
    y += 5;
  });
  
  y += 8;
  
  // Ambiente ideal
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('AMBIENTE IDEAL DE TRABALHO:', margin, y);
  y += 8;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  const ambienteLines = pdf.splitTextToSize(dominantProfile.ambiente, contentWidth - 10);
  pdf.text(ambienteLines, margin + 5, y);
  y += ambienteLines.length * 5 + 10;
  
  // Estresse
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('FATORES DE ESTRESSE:', margin, y);
  y += 8;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  pdf.text(dominantProfile.estresse, margin + 5, y);
  
  // ================== PÁGINA 5: PONTOS FORTES E DE ATENÇÃO ==================
  addNewPage();
  y = margin + 30;
  
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('PONTOS FORTES E DE DESENVOLVIMENTO', margin, y);
  y += 15;
  
  // Pontos Fortes
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(21, 128, 61);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PONTOS FORTES', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  
  dominantProfile.pontosFortes.forEach(ponto => {
    pdf.text(`✓ ${ponto}`, margin + 5, y);
    y += 7;
  });
  
  y += 15;
  
  // Pontos de Atenção
  pdf.setFillColor(254, 243, 199);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(180, 83, 9);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('PONTOS DE ATENÇÃO / DESENVOLVIMENTO', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  
  dominantProfile.pontosAtencao.forEach(ponto => {
    pdf.text(`⚠ ${ponto}`, margin + 5, y);
    y += 7;
  });
  
  y += 15;
  
  // Dicas de comunicação
  pdf.setFillColor(219, 234, 254);
  pdf.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
  pdf.setTextColor(30, 64, 175);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('COMO SE COMUNICAR COM ESTE PERFIL', margin + 5, y + 6);
  y += 15;
  
  pdf.setTextColor(50, 50, 50);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const comunicacaoLines = pdf.splitTextToSize(dominantProfile.comunicacao, contentWidth - 10);
  pdf.text(comunicacaoLines, margin + 5, y);
  
  // ================== PÁGINA 6: ASSINATURA ==================
  addNewPage();
  y = margin + 30;
  
  pdf.setFontSize(18);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(30, 64, 175);
  pdf.text('CONSIDERAÇÕES FINAIS', margin, y);
  y += 15;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(50, 50, 50);
  
  const consideracoes = [
    'Este relatório apresenta uma análise do perfil comportamental baseada na metodologia DISC.',
    'Os resultados refletem tendências comportamentais observáveis e preferências de atuação.',
    '',
    'É importante lembrar que:',
    '• O DISC mede comportamentos, não personalidade ou inteligência',
    '• Não existem perfis melhores ou piores - cada um tem seus pontos fortes',
    '• Os comportamentos podem ser adaptados conforme o contexto',
    '• Este relatório deve ser usado como ferramenta de autoconhecimento e desenvolvimento',
    '',
    'Recomenda-se que os insights deste relatório sejam utilizados para:',
    '• Autoconhecimento e desenvolvimento pessoal',
    '• Melhoria nas relações interpessoais',
    '• Adequação de funções e responsabilidades',
    '• Desenvolvimento de liderança e trabalho em equipe'
  ];
  
  consideracoes.forEach(line => {
    pdf.text(line, margin, y);
    y += 6;
  });
  
  y += 20;
  
  // Assinatura do Psicólogo
  if (psicologo) {
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, y, margin + 80, y);
    y += 5;
    
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(psicologo.nome, margin, y);
    y += 6;
    
    if (psicologo.crp) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`CRP: ${psicologo.crp}`, margin, y);
    }
  }
  
  // Data de geração
  y = pageHeight - 40;
  pdf.setFontSize(9);
  pdf.setTextColor(100, 116, 139);
  pdf.text(`Relatório gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, margin, y);
  pdf.text('G2eGente - Plataforma de Recrutamento Ético', margin, y + 5);
  
  return pdf;
};

export default function RelatorioDISCProfissional({ 
  open, 
  onClose, 
  invite, 
  psychologists = [],
  crpObrigatorio = false
}) {
  const [loading, setLoading] = useState(false);
  const [psicologoSelecionado, setPsicologoSelecionado] = useState(null);
  const [nomeManual, setNomeManual] = useState('');
  const [crpManual, setCrpManual] = useState('');
  const [usarManual, setUsarManual] = useState(psychologists.length === 0);

  const resultado = invite?.resultado?.disc_detalhado || invite?.resultado;
  const rawDiscProfile = invite?.resultado?.disc_profile || resultado?.resultado_dominante;
  const discProfile = rawDiscProfile ? rawDiscProfile[0] : null;
  
  if (!discProfile || !resultado) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resultado não disponível</DialogTitle>
          </DialogHeader>
          <p className="text-slate-500">
            O resultado do teste DISC não está disponível para este convite.
          </p>
          <DialogFooter>
            <Button onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const dominantProfile = DISC_PROFILES[discProfile];
  const scores = resultado.scores || { D: 25, I: 25, S: 25, C: 25 };
  const perfilCombinado = getPerfilCombinado(scores);

  const dadosRelatorio = {
    nome: invite.nome_destinatario,
    cargo: invite.cargo_funcao || '',
    dataAvaliacao: invite.responded_at 
      ? new Date(invite.responded_at).toLocaleDateString('pt-BR', { 
          day: '2-digit', month: 'long', year: 'numeric' 
        })
      : new Date().toLocaleDateString('pt-BR'),
    dominant: discProfile,
    scores: scores
  };

  const getPsicologoData = () => {
    if (usarManual) {
      if (!nomeManual.trim()) return null;
      return { nome: nomeManual.trim(), crp: crpManual.trim() || null };
    }
    if (psicologoSelecionado) {
      const psi = psychologists.find(p => p.id === psicologoSelecionado);
      return psi ? { nome: psi.nome, crp: psi.crp } : null;
    }
    return null;
  };

  const canGenerate = () => {
    const psi = getPsicologoData();
    if (!psi) return false;
    if (crpObrigatorio && !psi.crp) return false;
    return true;
  };

  const handleGenerate = async () => {
    const psicologo = getPsicologoData();
    if (!psicologo) {
      toast.error('Informe o responsável pelo relatório');
      return;
    }
    if (crpObrigatorio && !psicologo.crp) {
      toast.error('CRP é obrigatório para este relatório');
      return;
    }

    setLoading(true);
    try {
      const pdf = await generateProfessionalPDF(dadosRelatorio, psicologo);
      
      const fileName = `Relatorio_DISC_${invite.nome_destinatario.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success('Relatório PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-600" />
            Gerar Relatório DISC Profissional
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview dos dados */}
          <div className="bg-gradient-to-r from-violet-50 to-blue-50 rounded-lg p-4 border border-violet-200">
            <h4 className="font-semibold text-violet-800 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Dados do Avaliado
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span><strong>Nome:</strong> {invite.nome_destinatario}</span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <span><strong>Cargo:</strong> {invite.cargo_funcao || 'Não informado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span><strong>Data:</strong> {dadosRelatorio.dataAvaliacao}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-violet-500" />
                <span><strong>Perfil:</strong></span>
                <Badge className="bg-violet-100 text-violet-700">{rawDiscProfile} - {dominantProfile?.nome}</Badge>
              </div>
            </div>
          </div>

          {/* Prévia do perfil */}
          <div className="bg-white rounded-lg p-4 border">
            <h4 className="font-semibold text-slate-700 mb-3">Prévia do Perfil: {perfilCombinado.nome}</h4>
            <p className="text-sm text-slate-600 mb-4">{perfilCombinado.descricao}</p>
            
            {/* Mini gráfico */}
            <div className="flex justify-around items-end h-20 bg-slate-50 rounded p-2">
              {['D', 'I', 'S', 'C'].map((letra) => (
                <div key={letra} className="flex flex-col items-center">
                  <div 
                    className="w-8 rounded-t transition-all"
                    style={{ 
                      height: `${(scores[letra] || 25) * 0.6}px`,
                      backgroundColor: DISC_COLORS[letra]
                    }}
                  />
                  <span className="text-xs font-bold mt-1" style={{ color: DISC_COLORS[letra] }}>{letra}</span>
                  <span className="text-xs text-slate-500">{scores[letra]}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Conteúdo do relatório */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">O relatório incluirá:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>✓ Capa profissional com dados do avaliado</li>
              <li>✓ Explicação da metodologia DISC</li>
              <li>✓ Gráfico de perfil com intensidade dos fatores</li>
              <li>✓ Análise detalhada do perfil predominante</li>
              <li>✓ Pontos fortes e áreas de desenvolvimento</li>
              <li>✓ Dicas de comunicação e ambiente ideal</li>
              <li>✓ Assinatura do responsável (6 páginas)</li>
            </ul>
          </div>

          {/* Seleção de Psicólogo */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-slate-700">Responsável pelo Relatório</h4>
              {crpObrigatorio && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  CRP Obrigatório
                </Badge>
              )}
            </div>

            {psychologists.length > 0 && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    variant={!usarManual ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUsarManual(false)}
                  >
                    Psicólogo Cadastrado
                  </Button>
                  <Button
                    variant={usarManual ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUsarManual(true)}
                  >
                    Informar Manualmente
                  </Button>
                </div>
              </div>
            )}

            {!usarManual && psychologists.length > 0 ? (
              <div className="space-y-2">
                <Label>Selecione o Psicólogo</Label>
                <select
                  value={psicologoSelecionado || ''}
                  onChange={(e) => setPsicologoSelecionado(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Selecione...</option>
                  {psychologists.map((psi) => (
                    <option key={psi.id} value={psi.id}>
                      {psi.nome} - CRP: {psi.crp}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Responsável *</Label>
                  <Input
                    value={nomeManual}
                    onChange={(e) => setNomeManual(e.target.value)}
                    placeholder="Nome completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CRP {crpObrigatorio && '*'}</Label>
                  <Input
                    value={crpManual}
                    onChange={(e) => setCrpManual(e.target.value.toUpperCase())}
                    placeholder="Ex: 06/123456"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleGenerate}
            disabled={loading || !canGenerate()}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Gerando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Gerar PDF Profissional
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
